import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Config } from './config';
import { AuthService } from './auth';
import { Cart } from '../Models/cart';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/timeout';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Product } from '../Models/product';
import { GoogleAnalyticsEventsService } from "../Services/google-analytics";


/**
 * The CartService hides most gory details for consumers. In general
 * only getCart, addProduct and rmProduct are needed.
 * Carts are available to authenticated users, but also to anonymous users.
 * To this end, whenever a cart is needed by an anonymous user (i.e. a
 * product is added to the cart), the user is logged in as a temporary user,
 * and the cart is created. When the user actually logs in, the existing local
 * cart is merged with the server side cart.
 * When a user logs out, the local cart is removed.
 */
@Injectable()
export class CartService {

  private subject:Subject<Cart> = new BehaviorSubject<Cart>(null);
  private cart: Cart;

  constructor(private http: HttpClient,
              private auth: AuthService,
              private gaEventsService: GoogleAnalyticsEventsService,
              private config: Config) {
    this.auth.getUser().subscribe(user => { this.handleUserChange(user) });
  }

  /**
   * If a user is emitted from auth, we need to act accordingly. If
   * the user actually logged in, we may have to merge the existing
   * cart into the new user.
   */
  private handleUserChange(user) {

    // Handle user logout
    if (!user) {
      this.cart = null;
      this.subject.next(null);
    } else {
      // we have a user
      if (user._cart) {
        if (this.cart) {
          if (user._cart.id != this.cart.id) {
            this.mergeCart(user._cart, this.cart);
          }
        } else {
          if (user._links.cart) {
            this.http.get<Cart>(user._links.cart).subscribe(
              cart => {
                this.cart = cart;
                this.subject.next(cart);
              }
            )
          }
        }
      }
    }
  }

  private get(cartId): Observable<Cart> {
    return this.http.get<Cart>(this.config.base + 'carts/' + cartId + '/');
  }

  create(): Observable<Cart> {
    return this.http.post<Cart>(this.config.getURL('carts'), {'items': []}).map(
      cart => {
        this.cart = cart;
        this.subject.next(cart);

        return this.cart;
      }
    );
  }

  protected addToCartEvent(product) {
    this.gaEventsService.emitEvent(
      "Cart",
      "Add to cart",
      "Added product to cart",
      product.price,
    );
  }

  /**
   * If the user adds a product, this is the moment we must create an
   * actual cart, if none is yet created.
   */
  public addProduct(product: Product): Observable<Cart> {

    if (!this.cart || !this.cart.id) {

      // Uhoh, need to get some work done.
      return this.auth.loginTmp().map(
        user => { return user } ).mergeMap(
          user => this.http.post<Cart>(
            user._cart._links['add_item'],
            {'product': product.id }
          ).timeout(this.config.request_timeout).map(
            cart => {
              this.addToCartEvent(product);
              this.cart = cart;
              this.subject.next(cart);
              return <Cart>cart;
            }
          )
        );
    } else {

      return this.http.post<Cart>(
        this.cart._links['add_item'],
        {'product': product.id }).timeout(this.config.request_timeout).map(
          cart => {
            this.addToCartEvent(product);
            this.cart = cart;
            this.subject.next(cart);
            return <Cart>cart;
          })
    }
  }

  public rmProduct(product: Product): Observable<Cart> {

    if (this.cart) {

      return this.http.post<Cart>(
        this.cart._links['rm_item'],
        { 'product': product.id })
        .map(
          cart => {
            this.cart = cart;
            this.subject.next(cart);
            return <Cart>cart;
          })
    } else {
      // TODO: get cart?
    }
  }

  private mergeCart(tgtCart: Cart, srcCart: Cart): void {
    this.http.post<Cart>(tgtCart._links['merge'],
      { 'src': srcCart.id }
    ).subscribe(
      cart => {
        this.cart = cart;
        this.subject.next(cart)
      }
    );
  }

  /**
   * Return the cart as Observable.
   */
  public getCart(): Observable<Cart> {

    return this.subject.asObservable();
  }

  /**
   * total price of cart items excluding shipment etc
  */
  subtotal(): number {

    let reducer = (accumulator, item) => {
      let currentValue = item.price ? item.price : 0;
      return +accumulator + +currentValue;
    }

    let subtotal = 0;
    if (this.cart) {
      subtotal = this.cart.items.reduce(reducer, 0);
    }
    return subtotal;
  }


}



// WEBPACK FOOTER //
// ./src/app/Services/cart.ts